import { type FormEvent, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { Button } from "../../components/Button";
import { GoogleSignIn } from "../../components/GoogleSignIn";
import { Input } from "../../components/Input";
import { Logo } from "../../components/Logo";
import { Body, Heading, Muted } from "../../components/Typography";
import { ApiError, api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import styles from "./Login.module.css";

type LocationState = { from?: string } | null;

export const Login = () => {
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === "authenticated") {
    const from = (location.state as LocationState)?.from ?? "/stores";
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      const from = (location.state as LocationState)?.from ?? "/stores";
      navigate(from, { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.body?.code === "email_unverified") {
        await api.resendRegistrationCode(email);
        navigate("/register", { state: { verifyEmail: email, verifyPassword: password } });
        return;
      }
      setError(
        e instanceof ApiError
          ? (e.body?.error ?? e.message)
          : "Couldn't log in. Try again in a moment.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <Logo />
        </div>
        <div className={styles.header}>
          <Heading level={2}>Welcome back</Heading>
          <Body>
            <Muted>Log in to see your stores.</Muted>
          </Body>
        </div>

        <GoogleSignIn
          text="signin_with"
          onSignedIn={() =>
            navigate((location.state as LocationState)?.from ?? "/stores", { replace: true })
          }
          onEmailUnverified={(address) =>
            navigate("/register", { state: { verifyEmail: address } })
          }
        />

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="email">
              Email
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              invalid={error !== null}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="password">
              Password
            </label>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              invalid={error !== null}
            />
          </div>
          {error && (
            <div role="alert" className={styles.error}>
              {error}
            </div>
          )}
          <Button type="submit" disabled={submitting}>
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <div className={styles.footer}>
          New to Shobdo? <Link to="/register">Create an account</Link>
          <br />
          <Link to="/recover">Forgot your password?</Link>
        </div>
      </div>
    </main>
  );
};
